import type FieldDraft from '@UI/Pages/Subjects/Shared/FormData/FieldDraft';
import isFieldDraftEmpty from '@UI/Pages/Subjects/Shared/FormData/constraint/isFieldDraftEmpty';
import isFieldValueValid from '@UI/Pages/Subjects/Shared/FormData/constraint/isFieldValueValid';
import resolveFieldValueMessage from '@UI/Pages/Subjects/Shared/FormData/constraint/resolveFieldValueMessage';
import type SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';
import { AbstractValidatedClass, ValidatedClass, constraint } from '@verclam/form-validator';

type FieldValueFormArgs = {
	definition: SubjectFieldDefinitionView;
	value: FieldDraft;
	serverMessage: string | null;
};

const EMPTY_DRAFTS: Readonly<Record<string, FieldDraft>> = Object.freeze({
	text: '',
	long_text: '',
	date: '',
	number: null,
	boolean: false,
	choice: null,
	multiple_choice: [],
});

/**
 * The draft of one sheet field, validated by the definition it carries. A server error is held in
 * `serverMessage` and cleared by `setValue`; it never goes through `updateFieldsValidationsResult`,
 * which would switch off the validation of `value` for every instance of this class.
 */
@ValidatedClass()
class FieldValueFormData extends AbstractValidatedClass<FieldValueFormArgs> {
	public readonly definition: SubjectFieldDefinitionView;

	@constraint(isFieldValueValid, () => 'La valeur saisie est invalide.')
	public readonly value: FieldDraft;

	public readonly serverMessage: string | null;

	constructor(data: FieldValueFormArgs) {
		super(data);
		this.definition = data.definition;
		this.value = data.value;
		this.serverMessage = data.serverMessage;
		Object.freeze(this);
	}

	/** `initialValue` is the stored value of the field, `null` when it has none yet. */
	static createFor(
		definition: SubjectFieldDefinitionView,
		initialValue: FieldDraft = null,
	): FieldValueFormData {
		const value = initialValue ?? EMPTY_DRAFTS[definition.nature] ?? null;

		return new FieldValueFormData({ definition, value, serverMessage: null });
	}

	setValue(value: FieldDraft): this {
		return this.cloneWith({ value, serverMessage: null } as Partial<FieldValueFormArgs>);
	}

	withServerMessage(serverMessage: string): this {
		return this.cloneWith({ serverMessage } as Partial<FieldValueFormArgs>);
	}

	/** The server's message when there is one, else the client's once the errors are shown. */
	get errorMessage(): string | undefined {
		if (this.serverMessage !== null) {
			return this.serverMessage;
		}

		return this.getValidationMessage('value') === undefined
			? undefined
			: resolveFieldValueMessage(this.definition, this.value);
	}

	get isShownValid(): boolean {
		return this.serverMessage === null && this.isValid('value');
	}

	/** The value sent to the server, `null` when the field is left out of the payload. */
	toValue(): string | number | boolean | readonly string[] | null {
		if (typeof this.value === 'boolean') {
			return this.value;
		}

		if (isFieldDraftEmpty(this.value)) {
			return null;
		}

		return typeof this.value === 'string' ? this.value.trim() : this.value;
	}
}

export default FieldValueFormData;
